import { useState } from 'react';
import { AlertTriangle, Bell, PartyPopper, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { Announcement } from '@/hooks/useAnnouncements';

interface AnnouncementCardProps {
  announcement: Announcement;
  defaultExpanded?: boolean;
}

const priorityConfig = {
  urgent: {
    icon: AlertTriangle,
    label: 'Urgente',
    cardClass: 'border-l-4 border-l-destructive bg-destructive/5',
    badgeVariant: 'destructive' as const,
  },
  celebration: {
    icon: PartyPopper,
    label: 'Celebração',
    cardClass: 'border-l-4 border-l-green-500 bg-green-50 dark:bg-green-950/20',
    badgeVariant: 'default' as const,
  },
  normal: {
    icon: Bell,
    label: 'Informativo',
    cardClass: 'border-l-4 border-l-primary bg-primary/5',
    badgeVariant: 'secondary' as const,
  },
};

export function AnnouncementCard({ announcement, defaultExpanded = false }: AnnouncementCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const config = priorityConfig[announcement.priority] || priorityConfig.normal;
  const Icon = config.icon;
  const isLongContent = (announcement.content?.length || 0) > 100;
  
  const authorName = announcement.created_by_user?.display_name 
    || announcement.created_by_user?.first_name 
    || 'Sistema';
  
  const timeAgo = formatDistanceToNow(new Date(announcement.created_at), {
    addSuffix: true,
    locale: ptBR,
  });
  
  return (
    <Card 
      className={`${config.cardClass} transition-all hover:shadow-md ${isLongContent ? 'cursor-pointer' : ''}`}
      onClick={() => isLongContent && setExpanded(!expanded)}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <Icon className={`h-5 w-5 ${
              announcement.priority === 'urgent' ? 'text-destructive' :
              announcement.priority === 'celebration' ? 'text-green-600' :
              'text-primary'
            }`} />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="font-semibold text-foreground truncate">
                {announcement.title}
              </h3>
              {announcement.priority === 'urgent' && (
                <Badge variant={config.badgeVariant} className="text-xs">
                  {config.label}
                </Badge>
              )}
            </div>
            
            {announcement.content && (
              <p className={`text-sm text-muted-foreground mb-2 whitespace-pre-line ${!expanded ? 'line-clamp-2' : ''}`}>
                {announcement.content}
              </p>
            )}
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{authorName}</span>
                <span>•</span>
                <span>{timeAgo}</span>
              </div>
              
              {isLongContent && (
                <button 
                  className="flex items-center gap-1 text-xs text-primary hover:underline"
                  onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
                >
                  {expanded ? (
                    <>Ler menos <ChevronUp className="h-3 w-3" /></>
                  ) : (
                    <>Ler mais <ChevronDown className="h-3 w-3" /></>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
